import { Link } from "react-router-dom";
import { STATUS_INFO, APPROACHING_RESTRICTION_INFO } from "../../config/moderationPresentation";
import { DEFAULT_ENFORCEMENT_RULES_PATH } from "../../errors/errorMessages";
import "../../App.css";

// `status` is already the sanitized public vocabulary the backend returns
// (getAccountStanding in enforcementService.js) — copy for each value lives
// in moderationPresentation.js, never internal level names/scores.
//
// Hidden entirely for plain good_standing — no notice needed when nothing
// is wrong. The one exception is warningState "approaching_restriction"
// (LEVEL_1_WATCH): status is still "good_standing" there, but it's the
// genuinely missing tier-2 case — a restrained heads-up before anything is
// actually restricted.
//
// Suspended/Under Review/Banned all block generation outright (see each
// status's message in moderationPresentation.js) and share the danger
// treatment; Limited is the only restricted tier that doesn't block
// generation, so it's the sole "warning" (caution, not danger) tier.
const LED_BY_PILL_STATUS = {
  approaching_restriction: "accent",
  limited: "warning",
  under_review: "danger",
  suspended: "danger",
  banned: "danger",
};

// Stage D applies this exact rate/concurrency/cooldown treatment uniformly
// to every Limited account today (no per-actor restriction-scope tracking
// yet — see generationThrottleService.js) — so this is accurate, shipped
// behavior for any Limited user, safe to state as static copy rather than
// backend-fetched per-request data.
const LIMITED_RESTRICTIONS = [
  { label: "Battle generation", value: "Reduced rate" },
  { label: "Image regeneration", value: "Longer wait between attempts" },
  { label: "Generation", value: "One request at a time" },
];

// Suspended/Under Review both mean generation is fully blocked (see
// evaluateRestriction in enforcementService.js) — a flat "unavailable" row,
// deliberately NOT Limited's rate/concurrency breakdown, which describes a
// throttled-but-working state this isn't.
const SUSPENDED_RESTRICTIONS = [{ label: "Generation", value: "Unavailable" }];

export default function AccountStandingSection({ standing }) {
  if (!standing) return null;
  const approaching = standing.status === "good_standing" && standing.warningState === "approaching_restriction";
  if (standing.status === "good_standing" && !approaching) return null;

  const pillStatus = approaching ? "approaching_restriction" : standing.status;
  const info = approaching ? APPROACHING_RESTRICTION_INFO : (STATUS_INFO[standing.status] ?? STATUS_INFO.limited);
  const led = LED_BY_PILL_STATUS[pillStatus] ?? "warning";
  const restrictions = pillStatus === "limited"
    ? LIMITED_RESTRICTIONS
    : (pillStatus === "suspended" || pillStatus === "under_review")
      ? SUSPENDED_RESTRICTIONS
      : null;

  return (
    <div className={`account-card account-standing-card led--${led}`}>
      <div className="account-card-header">
        <h3 className="account-card-title">Account Standing</h3>
        <span className={`status-pill status-pill--${pillStatus}`}>
          <span className="status-dot" />
          {info.label}
        </span>
      </div>
      <p className="account-standing-message">{info.message}</p>
      {restrictions && (
        <ul className="account-restriction-list">
          {restrictions.map(({ label, value }) => (
            <li key={label}>
              <span>{label}</span>
              <span>{value}</span>
            </li>
          ))}
        </ul>
      )}
      {standing.restrictionEndsAt && (
        <p className="account-standing-until">
          Expected to lift {new Date(standing.restrictionEndsAt).toLocaleString()}
        </p>
      )}
      <Link to={DEFAULT_ENFORCEMENT_RULES_PATH} className="account-inline-link">View Rules ›</Link>
    </div>
  );
}
